//YOU CANNOT REORDER THE IMPORTS OF THIS FILE, FULL CALENDAR IMPORT NEEDS TO BE FIRST
import FullCalendar, { EventSourceInput } from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useGlobalStore } from "../../../api/globalStores";
import { definitions } from "../../../api/supabase/client";
import IconButtonPrimary from "../../../Common/Buttons/IconButtonPrimary";
import IconPlus from "../../../Common/Icons/IconPlus";
import {
  getClassTimings,
  getTimezoneOffsetString,
  getTimezoneRegionString,
} from "../../../Common/Utility/helpers";
import Modal from "../../../Common/Utility/Modal";
import EventInfoCard from "../EventInfoCard";
import "./Calendar2.css";
import { CreateClassDialog } from "./CreateClassDialog";
import { ManageTeachersDialog } from "./ManageTeachersDialog";
import { TimeSlotDialog } from "./TimeSlotDialog";

const ROW_HEIGHT = "4rem";

export default observer(Calendar2);

function Calendar2() {
  const [openAddTimeSlotDialog, setOpenAddTimeSlotDialog] = useState(false);

  const globalStores = useGlobalStore();
  const [eventInfoOpen, setEventInfoOpen] = useState(false);

  const [levels, setLevels] = useState<definitions["Level"][]>([]);
  const [openCreateClassDialog, setOpenCreateClassDialog] = useState(false);
  const [openManageTeachersDialog, setOpenManageTeachersDialog] =
    useState(false);

  const navigate = useNavigate();

  const [timeSlotId, setTimeSlotId] = useState<undefined | number>();

  const [newClass, setNewClass] = useState<{
    startDate?: Date;
    endDate?: Date;
    levelId?: number;
  }>({});

  useEffect(() => {
    const run = async () => {
      //TODO: Group promises for reduced loading times
      const allLevels = await globalStores.teacherStore.getLevels();
      setLevels(allLevels);
    };

    run();
  }, [globalStores.teacherStore]);

  const handleCreateClass = () => {
    if (!newClass.levelId || !newClass.startDate || !newClass.endDate) {
      throw toast.error("Level, Start Date and End Date must be properly set");
    }
    //TODO: Handle errors
    toast.promise(
      globalStores.teacherStore.createClass(
        newClass.levelId,
        newClass.startDate,
        newClass.endDate
      ),
      {
        loading: "Creating Class",
        success: (newClass) => {
          setOpenCreateClassDialog(false);
          return (
            <span>
              Class <b>{newClass.class.id}</b> created successfully!
            </span>
          );
        },
        // success: "Class created successfully!",
        error: (e: any) => e.message,
      },
      {
        success: {
          duration: 5000,
        },
      }
    );
  };

  const handleEventClickWithToast = async (arg: any) => {
    toast.promise(handleEventClick(arg), {
      success: "Timeslot loaded",
      loading: "Loading Timeslot...",
      error: (e) => e.message,
    });
  };

  const handleEventClick = async (arg: any) => {
    await handleOpenEventInfo(
      arg.event.extendedProps.classID,
      arg.event.extendedProps.timeSlotID,
    );

    if (arg.jsEvent.ctrlKey) {
      navigate("/teacher/lesson");
    }
  };

  const handleOpenEventInfo = async (
    classId: number,
    newTimeSlotId: number,
  ) => {
    try {
      await globalStores.curTalkingParty.joinActiveLessonForClass(classId);
    } catch (err: any) {
      // If there's no active lesson right now, we still want to open the info
      // dialog so the teacher can review/edit the schedule — just without a
      // live room. Re-throw any other error so it surfaces to the toast.
      if (!String(err?.message ?? "").toLowerCase().includes("no class to join")) {
        throw err;
      }
    }

    setTimeSlotId(newTimeSlotId);
    globalStores.curClass.init(classId);
    setEventInfoOpen(true);
  };

  const timeSlotsEvents: EventSourceInput =
    globalStores.teacherStore.myClasses.flatMap((curClass) => {
      return curClass.timeSlots.flatMap((timeSlot) => {
        return getClassTimings(curClass, timeSlot);
      });
    });

  return (
    <>
      <TimeSlotDialog
        open={openAddTimeSlotDialog}
        onClose={() => setOpenAddTimeSlotDialog(false)}
      />
      <Modal open={eventInfoOpen} onClose={() => setEventInfoOpen(false)}>
        <EventInfoCard
          handleClose={() => setEventInfoOpen(false)}
          timeSlotId={timeSlotId}
        />
      </Modal>
      <Modal
        open={openManageTeachersDialog}
        onClose={() => setOpenManageTeachersDialog(false)}
      >
        <ManageTeachersDialog />
      </Modal>
      <Modal
        open={openCreateClassDialog}
        onClose={() => setOpenCreateClassDialog(false)}
      >
        <CreateClassDialog
          levels={levels}
          setNewClass={setNewClass}
          newClass={newClass}
          handleCreateClass={handleCreateClass}
        />
      </Modal>
      <div className="flex flex-row justify-between">
        <div className="flex flex-col justify-start items-start bg-grey">
          <div className="text-heading1 font-semibold">Calendar</div>
          <div className=" font-semibold">
            <p>Current Timezone:</p>
            <p>
              {getTimezoneOffsetString()} (
              {getTimezoneRegionString().replace(/_/g, " ")})
            </p>
          </div>
        </div>
        <div className="flex justify-end items-start">
          <div className="flex gap-2">
            <div className="stroke-brij-White">
              <IconButtonPrimary
                onClick={() => setOpenManageTeachersDialog(true)}
                text="Manage Teachers"
                color="mud"
              />
            </div>
            <div className="stroke-brij-White">
              <IconButtonPrimary
                onClick={() => setOpenCreateClassDialog(true)}
                text="New Class"
                icon={<IconPlus />}
                color="mud"
              />
            </div>
            <div className="stroke-brij-White">
              <IconButtonPrimary
                onClick={() => setOpenAddTimeSlotDialog(true)}
                text="Add Time Slot"
                icon={<IconPlus />}
                color="mud"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-scroll w-full p-6">
        <FullCalendar
          plugins={[
            dayGridPlugin,
            listPlugin,
            timeGridPlugin,
            interactionPlugin,
          ]}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
          }}
          slotLabelFormat={{
            hour: "numeric",
            hour12: true,
          }}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          }}
          events={timeSlotsEvents}
          eventClick={handleEventClickWithToast}
          height="100%"
          eventMouseEnter={(e) => {
            e.el.style.cursor = "pointer";
          }}
          eventMouseLeave={(e) => {
            e.el.style.cursor = "auto";
          }}
          // defaultTimedEventDuration="01:00"
        />
      </div>
    </>
  );
}
